import React, { memo, MutableRefObject, useMemo } from "react";

// Third party
import moment, { Moment } from "moment";

// MUI
import { Box, Button, CircularProgress, Dialog, Grid, Paper, Stack, Typography } from "@mui/material";

// Services
import { useGridApiRef } from "@mui/x-data-grid-premium";

// Helpers
import { displaySkeleton } from "@/Settings/helpers/SystemSettings/appendingConfiguration";
import { or } from "@Common/helpers/sonar";

// Table Layouts
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";

// Layouts
import DateDiff from "@Layouts/components/DateDiff";
import DataTableDateTimePicker from "@/Layouts/components/DateTimePicker/DataTableDateTimePicker";
import LinkStyledButton from "@Layouts/components/Buttons/LinkStyledButton";
import SimpleIconButton from "@/Layouts/components/Buttons/SimpleIconButton";
import UpcomingPayPlanLoader from "@/Layouts/components/Skeletons/UpcomingPayPlanLoader";

// Custom components
import AssignmentMessages from "@/PayPlans/components/Assignments/AssignmentMessages";
import SelectPlanDialog from "@/PayPlans/components/Assignments/SelectPlanDialog";
import { RemoveAssignment } from "../RemoveAssignment";

// Icons
import DraftIcon from "@PayPlans/assets/icons/upcoming/Draft.svg";
import UnDraftIcon from "@PayPlans/assets/icons/upcoming/UnDraft.svg";
import PencilIcon from "@PayPlans/assets/icons/upcoming/Pencil.svg";

// Images
import clsx from "clsx";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";

// Constants
import { ONE_HUNDRED_SEVENTY, ONE_HUNDRED_SIXTY, ONE_HUNDRED_THIRTY, TWO_HUNDRED_TWENTY } from "@/Constants/tables";
import { GridColDef } from "@mui/x-data-grid-pro";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { ICommissionCycle } from "@Common/api/types/CommissionCycle";
import { IPayPlanAssignment, IUser } from "@Common/interfaces/user";
import { DATE_FORMAT } from "@/Constants/date";

const getUpcomingIcon = (status: string) => {
	switch (status) {
		case "INPROGRESS":
			return DraftIcon;
		case "DRAFT":
			return UnDraftIcon;
		default:
			return DraftIcon;
	}
};

type TInputEditDateTimeCellProps = Readonly<{
	id: string;
	error: boolean;
	fromDate: Moment;
	toDate: Moment;
	commissionCycle: ICommissionCycle;
	value: string | undefined;
	upcomingData: IUser;
	updateSelectedPlan: (input: unknown) => void;
	apiRef: MutableRefObject<GridApiPremium>;
}>;
const InputEditDateTimeCell = memo((props: TInputEditDateTimeCellProps) => {
	const { error, fromDate, toDate, commissionCycle } = props;
	const [dateValue, setDateValue] = React.useState<Date | string | undefined>(props.value);

	const onChangeHandle = (value: Date | string) => {
		const inputValue = moment(value).utc(false).format();
		const isoDate = new Date(inputValue).toISOString();
		setDateValue(new Date(inputValue));

		// Debounce or batch updates to prevent excessive re-renders
		props.updateSelectedPlan({
			effectiveDate: isoDate,
			locationId: props.upcomingData?.locationId,
			user: { _id: props.upcomingData._id },
			payPlan: {
				name: props.upcomingData?.upcomingPayPlanAssignment?.planId?.name,
				_id: props.upcomingData?.upcomingPayPlanAssignment?.planId?._id,
			},
		});
	};

	return (
		<DataTableDateTimePicker
			value={moment(dateValue).utc(false)}
			minDateTime={fromDate.utc(false)}
			maxDateTime={toDate}
			shouldDisableDate={(date) => {
				const value = moment(commissionCycle.startDate).utc();
				return date.date() !== value.date();
			}} // Disable all days except the 1st of the month
			defaultCalendarMonth={fromDate}
			disableIgnoringDatePartForTimeValidation
			disableHighlightToday
			disablePast
			customizedInput={true}
			adapter={"moment"}
			variant={"standard"}
			views={["year", "month", "day"]}
			viewRenderers={{
				hours: null,
				minutes: null,
				seconds: null,
			}}
			customizedInputClasses={"w-[175px]"}
			hideTabs
			useCustomAnchorEl
			customAnchorEl={props.apiRef?.current?.rootElementRef?.current}
			format={DATE_FORMAT.monthAbbrDatePadded}
			error={error}
			onChange={onChangeHandle}
		/>
	);
});

// Helpers functions
function getHeaderClassName(param: boolean): string {
	return param ? "[&_.MuiDataGrid-columnHeaderTitle]:!text-primary" : "[&_.MuiDataGrid-columnHeaderTitle]:!text-default";
}

function runShowRowFeedback(showRowFeedback: boolean, id: string, apiRef: MutableRefObject<GridApiPremium>) {
	return showRowFeedback
		? displaySkeleton({ ids: [id], action: "display", apiRef })
		: displaySkeleton({ ids: [id], action: "success", apiRef });
}

function getTableRows(
	props: TPayPlanTableProps,
	upcomingStatus: string,
	{
		isDraftDefaultValue,
		typeOfPayPlanAssignmentValue,
		isVisibleDTL,
	}: {
		isDraftDefaultValue: boolean;
		typeOfPayPlanAssignmentValue: keyof Pick<IUser, "upcomingPayPlanAssignment" | "currentPayPlanAssignment">;
		isVisibleDTL: boolean;
	}
) {
	return props?.payPlanAssignment
		? [
				{
					// TODO the line below is a patch; needs to be fixed when group assignment is completed
					_id: props?.payPlanAssignment?._id ?? props?.payPlanAssignment?.planId?._id,
					name:
						upcomingStatus === "DRAFT" ? (
							<LinkStyledButton
								text={props?.payPlanAssignment?.planId?.name}
								endIcon={<img src={PencilIcon} alt="pencil-icon" />}
								onClick={() => props.onClickChangePayPlan(true)}
								disabled={props.showRowFeedback}
							/>
						) : (
							<LinkStyledButton style={"regular"} text={props?.payPlanAssignment?.planId?.name} />
						),
					assignmentDate: moment(new Date(props?.payPlanAssignment?.assignmentDate))
						.utc()
						.format(DATE_FORMAT.monthAbbrDatePadded),
					assignedBy: (
						<Typography sx={{ fontSize: "14.5px", fontFamily: "PTSansProNarrow, sans-serif", width: "120px" }} noWrap>
							{props.payPlanAssignment?.assignedBy ?? ""}
						</Typography>
					),
					effectiveDate: isDraftDefaultValue ? (
						<LinkStyledButton
							text={
								<span style={{ display: "inline-block" }}>
									{moment(new Date(props.payPlanAssignment?.effectiveDate)).utc().format(DATE_FORMAT.monthAbbrDatePadded)}
								</span>
							}
							endIcon={<img src={String(PencilIcon)} alt="pencil-icon" />}
							disabled={props.showRowFeedback}
						/>
					) : (
						<>
							<Box style={{ display: "inline-block" }}>
								{moment(new Date(props.user?.[typeOfPayPlanAssignmentValue]?.effectiveDate ?? ""))
									.utc()
									.format(DATE_FORMAT.monthAbbrDatePadded)}
							</Box>
							<Box className="!ml-[16px] !inline-block">
								<DateDiff
									noTooltip
									classNameClock="mt-[-2.5px] mr-[4px]"
									marginLeft="0"
									date={moment(new Date(props.user?.[typeOfPayPlanAssignmentValue]?.effectiveDate ?? ""))
										.utc()
										.format(DATE_FORMAT.monthAbbrDatePadded)}
									maxWidth={"100px"}
								/>
							</Box>
						</>
					),
					acknowledgementDate: (
						<Stack direction="row" alignItems={"center"} className="!text-[14.5px] !font-sans !block">
							<AssignmentMessages
								data={props.user}
								status={props?.payPlanAssignment?.latestPayPlanAcknowledgementStatus?.status}
								statusDate={props?.payPlanAssignment?.latestPayPlanAcknowledgementStatus?.statusDate}
								isVisibleDTL={isVisibleDTL}
								disableSendEmail={props.disableSendEmail}
								showRowFeedback={props.showRowFeedback}
								sendEmailMethod={props.sendEmailMethod}
							/>
						</Stack>
					),
				},
		  ]
		: [];
}

function getIconButtonOpacity(upcomingStatus: string, showRowFeedback: boolean, noData: boolean) {
	return ["COMPLETED", "EXPIRED"].includes(upcomingStatus) || showRowFeedback || noData ? 0.5 : 1;
}

function getEvaluateParams<A = boolean, B = object, C = object>(expression: A, optionA: B, optionB: C): B | C {
	return expression ? optionA : optionB;
}

// Constants
const baseStyles = {
	maxWidth: "100%",
};

interface ExtendedPayPlanAssignment extends Omit<IPayPlanAssignment, "planId"> {
	planId: {
		_id: string;
		name: string;
		endDate: string;
	};
	latestPayPlanAcknowledgementStatus: {
		status: string;
		statusDate: string;
	};
}

type TPayPlanTableProps = Readonly<{
	payPlanAssignment: ExtendedPayPlanAssignment;
	commissionCycle: ICommissionCycle;
	visibleFrame: boolean;
	isLoading: boolean;
	title: string;
	noDataMessage: string;
	headersInBlue: boolean;
	deleteBtn: boolean;
	changePayPlanBtn: boolean;
	disableChangePayPlanBtn: boolean;
	changePayPlanBtnClass: string;
	openDialog: boolean;
	user: IUser;
	payPlanSelected: string;
	phone: object;
	email: string;
	disableSendEmail: boolean;
	showRowFeedback: boolean;
	visibleDlt: boolean;
	isModifiable: boolean;
	onClickChangePayPlan: (value?: boolean) => void;
	handleCloseDialog: () => void;
	assignPayPlan: () => void;
	sendEmailMethod: () => void;
	deletePlan: () => void;
	updateSelectedPlan: (inputData: unknown) => void;
	updateUpcomingPayPlan: boolean;
	hireDate: string;
	payPlanPosition: string;
	payPlanButtonText: string;
	isSimpleButtonDisabled: boolean;
	removeDisabled: boolean;
}>;
export default function PayPlanTable(props: TPayPlanTableProps) {
	const apiRef = useGridApiRef();
	const upcomingStatus = props?.payPlanAssignment?.latestPayPlanAcknowledgementStatus?.status;

	const assignedBy = props?.user?.upcomingPayPlanAssignment?.assignedBy;

	const headerClassName = getHeaderClassName(props.headersInBlue);

	const isUpcomingPayPlan = props.title === "Upcoming Pay Plan";

	const conditionalStyles = {
		...baseStyles,
		borderColor: "var(--gray-row-color)",
		backgroundColor: "var(--gray-row-color)",
		borderRadius: "20px",
		marginTop: "3px",
		paddingLeft: "16px",
		paddingTop: 1.5,
	};

	const sameProperties: Partial<GridColDef> = {
		headerClassName: `${headerClassName} [&_.MuiDataGrid-columnHeaderTitle]:!font-medium`,
		renderCell: (params) => <CustomCell value={params.value} hasTooltip maxWith={params.colDef.width ?? 0} fontSize="14.5px" />,
	};

	const isDraftDefault = () => upcomingStatus === "DRAFT" && assignedBy !== "Default" && !props?.hireDate;
	const typeOfPayPlanAssignment = (): keyof Pick<IUser, "upcomingPayPlanAssignment" | "currentPayPlanAssignment"> => {
		return isUpcomingPayPlan ? "upcomingPayPlanAssignment" : "currentPayPlanAssignment";
	};

	const isVisibleDTL = () => props.visibleDlt;

	const columns: GridColDef[] = useMemo(() => {
		return [
			{ field: "_id", headerName: "ID", width: 0, hide: true },
			{
				field: "name",
				headerName: "Pay Plan ID",
				width: ONE_HUNDRED_THIRTY,
				sortable: false,
				...sameProperties,
				valueGetter: (_, row) => row?.name,
			},
			{
				field: "assignmentDate",
				headerName: "Assignment Date",
				width: ONE_HUNDRED_SEVENTY,
				sortable: false,
				...sameProperties,
			},
			{
				field: "assignedBy",
				headerName: "Assigned By",
				width: ONE_HUNDRED_SIXTY,
				sortable: false,
				valueGetter: (_, row) => row?.assignedBy,
				...sameProperties,
			},
			{
				field: "effectiveDate",
				headerName: "Effective Date",
				width: TWO_HUNDRED_TWENTY,
				sortable: false,
				headerClassName: sameProperties.headerClassName,
				renderCell: (params) => {
					return isDraftDefault() ? (
						<InputEditDateTimeCell
							id={params?.row?.id}
							value={props.user?.[typeOfPayPlanAssignment()]?.effectiveDate}
							fromDate={moment(new Date(props?.payPlanAssignment?.assignmentDate)).utc(false)}
							toDate={moment(new Date(props?.payPlanAssignment?.planId?.endDate)).utc(false)}
							error={false}
							upcomingData={props?.user}
							updateSelectedPlan={props.updateSelectedPlan}
							commissionCycle={props.commissionCycle}
							apiRef={apiRef}
						/>
					) : (
						<CustomCell value={params.value} hasTooltip maxWith={params.colDef.width ?? 0} fontSize="14.5px" />
					);
				},
				valueGetter: (_, row) => row?.effectiveDate,
			},
			{
				field: "acknowledgementDate",
				headerName: "Acknowledgement Status",
				width: 294,
				sortable: false,
				headerClassName: `${headerClassName} [&_.MuiDataGrid-columnHeaderTitle]:!font-medium`,
				cellClassName: "!block",
				renderCell: (params) => params?.row?.acknowledgementDate,
				valueGetter: (_, row) => row?.acknowledgementDate,
			},
		];
	}, [
		sameProperties,
		props.user,
		props.payPlanAssignment?.assignmentDate,
		props.payPlanAssignment?.planId.endDate,
		props.updateSelectedPlan,
		props.commissionCycle,
		headerClassName,
		apiRef,
	]);

	const tableRows = getTableRows(props, upcomingStatus, {
		isDraftDefaultValue: isDraftDefault(),
		typeOfPayPlanAssignmentValue: typeOfPayPlanAssignment(),
		isVisibleDTL: isVisibleDTL(),
	});

	/*********************************************** HANDLERS ***********************************************/
	const handleSwitchStatus = () => {
		const input = {
			locationId: props.user?.locationId,
			status: upcomingStatus === "DRAFT" ? "SENT" : "DRAFT",
			packageId: props.user?.upcomingPayPlanAssignment?.packageId,
			user: { _id: props?.user?._id, locationId: props?.user?.locationId, position: props?.user?.position },
			payPlan: {
				name: props.user?.upcomingPayPlanAssignment?.planId?.name,
				_id: props.user?.upcomingPayPlanAssignment?.planId?._id,
			},
		};
		props.updateSelectedPlan(input);
	};

	runShowRowFeedback(props.showRowFeedback, props?.payPlanAssignment?._id, apiRef);

	if (props.isLoading) {
		return <UpcomingPayPlanLoader deleteBtn={props.deleteBtn} changePayPlanBtn={props.changePayPlanBtn} />;
	}

	return (
		<Grid
			container
			direction="column"
			sx={getEvaluateParams(props.visibleFrame, { ...conditionalStyles }, {})}
			justifyContent={"space-between"}
			className={"!px-[16px]"}
		>
			<Grid item container xs={12} direction="row" justifyContent={"space-between"} marginBottom={"16px"}>
				<Grid item container xs={6}>
					<Grid item>
						<Typography
							className={clsx({
								"page-title": true,
								[`${getEvaluateParams(props?.headersInBlue, "!text-primary", "!text-default")}`]: true,
							})}
						>
							{props?.title}
						</Typography>
					</Grid>
					<Grid item>
						{props?.isModifiable && (
							<SimpleIconButton
								sx={{
									opacity: getIconButtonOpacity(upcomingStatus, props.showRowFeedback, !props?.payPlanAssignment),
								}}
								className={"btn-add"}
								color={"var(--primary-color)"}
								icon={<img src={String(getUpcomingIcon(upcomingStatus))} alt="icon" width={"15.57px"} height={"14.57px"} />}
								disabled={or(
									props.showRowFeedback,
									props.isSimpleButtonDisabled,
									!props?.payPlanAssignment,
									props?.payPlanAssignment?.latestPayPlanAcknowledgementStatus?.status === "COMPLETED"
								)}
								onClick={handleSwitchStatus}
							/>
						)}
					</Grid>
					<Grid item sx={{ color: "#6c6c6c", paddingTop: "5px" }}>
						{props?.showRowFeedback && <CircularProgress color="inherit" size={20} />}
					</Grid>
				</Grid>
				{props.changePayPlanBtn && (
					<Grid item xs={6}>
						<Paper
							elevation={0}
							sx={{ display: "flex", justifyContent: "flex-end", paddingTop: "2px", background: "transparent" }}
						>
							<Button
								id="select-plan-dialog-assign-btn"
								variant="contained"
								aria-controls="select-plan-dialog-assign-btn-menu"
								disabled={props.disableChangePayPlanBtn ?? props.showRowFeedback}
								className={props.changePayPlanBtnClass}
								onClick={() => props.onClickChangePayPlan()}
								sx={{ width: "auto" }}
							>
								<Typography className="select-plan-dialog-assign-btn-text">{props.payPlanButtonText}</Typography>
							</Button>
							<Dialog
								open={props.openDialog}
								disableEscapeKeyDown
								onClose={props.handleCloseDialog}
								className="select-plan-dialog [&>.MuiDialog-container>.MuiPaper-root]:!rounded-[10px] "
							>
								<SelectPlanDialog
									data={props.user}
									closeCallback={props.handleCloseDialog}
									hireDate={props.hireDate}
									assignPayPlan={props.assignPayPlan}
									updateSelectedPlan={props.updateSelectedPlan}
									updateUpcomingPayPlan={props.updateUpcomingPayPlan}
									payPlanSelected={props.payPlanSelected}
									payPlanPosition={props.payPlanPosition}
									locationId={props.user?.locationId}
								/>
							</Dialog>
						</Paper>
					</Grid>
				)}
			</Grid>

			<Grid item container xs={12} direction="row" alignItems={"end"}>
				<Grid item xs={10.5}>
					<DataSmartTable
						disableHScrollCorrection
						apiRef={apiRef}
						columns={columns}
						rows={tableRows}
						headerHeight={35}
						rowHeight={40}
						disableColumnMenu
						disableRowSelectionOnClick
						disableMultipleRowSelection
						checkboxSelection={false}
						autoHeight
						columnVisibilityModel={{
							_id: false,
						}}
						slots={{
							noRowsOverlay: NoRowsOverlay,
						}}
						slotProps={{
							noRowsOverlay: {
								title: props.noDataMessage,
							},
						}}
						sx={{
							"& .MuiDataGrid-row": {
								backgroundColor: getEvaluateParams(props?.visibleFrame, "transparent !important", "white !important"),
							},
							"& .MuiDataGrid-cell": {
								// Keep it as flex to align items in the center
								display: "flex",
								alignItems: "center",
								paddingTop: "0px !important",
								paddingBottom: "0px !important",
							},
							"& .MuiDataGrid-row:hover .MuiDataGrid-cell": {
								backgroundColor: getEvaluateParams(
									props?.title === "Upcoming Pay Plan",
									"white !important",
									"#f7f7f7 !important"
								),
							},
							...(props?.visibleFrame ? { ...conditionalStyles, borderRadius: 0, paddingTop: 0, paddingLeft: "0" } : {}),
						}}
						disableColumnReorder={true}
						hideFooter
						customProps={{
							tableContainerStyle: { height: "100%", width: "100%", minWidth: "200px" },
							tableClassName: {
								"!font-montserrat !text-[16px] !font-medium": true,
								"[&_.MuiDataGrid-columnHeaders>div:first-child]:!bg-gray-row-color": props?.visibleFrame ?? false,
								absolute: true,
							},
							expandableRows: false,
							hide: false,
						}}
					/>
				</Grid>
				<Grid item width={100}>
					<RemoveAssignment
						visible={!!tableRows?.length && props.deleteBtn}
						disabled={props.removeDisabled}
						onDeleteAction={props.deletePlan}
					/>
				</Grid>
			</Grid>
		</Grid>
	);
}
